export type LineItemDateTimeOptions = {
  timezone?: string;
};

export function formatMadBuyLineItemDateTimeInput(value: string | undefined, timezone = 'UTC') {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return formatDateTimeParts(getDateTimeParts(date, timezone));
}

export function formatDateTimeInputForPayload(value: string, timezone = 'UTC') {
  if (!value) {
    return '';
  }

  const parsedInput = parseDateTimeInput(value);
  if (parsedInput) {
    return formatDateTimeParts(zonedInputToUtcParts(parsedInput, timezone));
  }

  throw new RangeError('Invalid line item date-time input');
}

type DateTimeParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
};

export function parseDateTimeInput(value: string): DateTimeParts | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value);
  if (!match) {
    return null;
  }

  const [, year, month, day, hour, minute] = match;
  if (!year || !month || !day || !hour || !minute) {
    return null;
  }

  const parsed = {
    year: Number(year),
    month: Number(month),
    day: Number(day),
    hour: Number(hour),
    minute: Number(minute),
  };

  if (
    parsed.month < 1 ||
    parsed.month > 12 ||
    parsed.day < 1 ||
    parsed.day > 31 ||
    parsed.hour < 0 ||
    parsed.hour > 23 ||
    parsed.minute < 0 ||
    parsed.minute > 59
  ) {
    return null;
  }

  const roundTripDate = new Date(
    Date.UTC(parsed.year, parsed.month - 1, parsed.day, parsed.hour, parsed.minute),
  );
  const roundTrip = formatDateTimeParts({
    year: roundTripDate.getUTCFullYear(),
    month: roundTripDate.getUTCMonth() + 1,
    day: roundTripDate.getUTCDate(),
    hour: roundTripDate.getUTCHours(),
    minute: roundTripDate.getUTCMinutes(),
  });

  return roundTrip === value ? parsed : null;
}

function zonedInputToUtcParts(value: DateTimeParts, timezone: string): DateTimeParts {
  const resolvedTimezone = resolveTimezone(timezone);
  let utcMs = Date.UTC(value.year, value.month - 1, value.day, value.hour, value.minute);

  for (let index = 0; index < 3; index += 1) {
    const rendered = getDateTimeParts(new Date(utcMs), resolvedTimezone);
    const renderedMs = Date.UTC(
      rendered.year,
      rendered.month - 1,
      rendered.day,
      rendered.hour,
      rendered.minute,
    );
    const targetMs = Date.UTC(value.year, value.month - 1, value.day, value.hour, value.minute);
    utcMs -= renderedMs - targetMs;
  }

  const rendered = getDateTimeParts(new Date(utcMs), resolvedTimezone);
  if (!dateTimePartsEqual(rendered, value)) {
    throw new RangeError('The local date-time does not exist in the selected timezone');
  }

  const date = new Date(utcMs);
  return {
    year: date.getUTCFullYear(),
    month: date.getUTCMonth() + 1,
    day: date.getUTCDate(),
    hour: date.getUTCHours(),
    minute: date.getUTCMinutes(),
  };
}

function dateTimePartsEqual(left: DateTimeParts, right: DateTimeParts) {
  return (
    left.year === right.year &&
    left.month === right.month &&
    left.day === right.day &&
    left.hour === right.hour &&
    left.minute === right.minute
  );
}

// Formatters are cached per timezone: schedule conversion resolves parts for many instants.
const zonedPartsFormats = new Map<string, Intl.DateTimeFormat>();

function zonedPartsFormat(timezone: string) {
  let format = zonedPartsFormats.get(timezone);
  if (!format) {
    // Parsed back into numbers, so the locale is fixed rather than the display locale.
    format = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    zonedPartsFormats.set(timezone, format);
  }
  return format;
}

export function isValidTimezone(timezone: string) {
  try {
    zonedPartsFormat(timezone);
    return true;
  } catch {
    return false;
  }
}

/** Wall-clock parts of `date` in `timezone` (UTC when the timezone is unknown). */
export function getDateTimeParts(date: Date, timezone: string): DateTimeParts {
  const parts = zonedPartsFormat(resolveTimezone(timezone)).formatToParts(date);

  const part = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((item) => item.type === type)?.value ?? '0');
  const hour = part('hour');

  return {
    year: part('year'),
    month: part('month'),
    day: part('day'),
    hour: hour === 24 ? 0 : hour,
    minute: part('minute'),
  };
}

function formatDateTimeParts(value: DateTimeParts) {
  return `${String(value.year)}-${pad2(value.month)}-${pad2(value.day)}T${pad2(value.hour)}:${pad2(
    value.minute,
  )}`;
}

function resolveTimezone(timezone: string) {
  return isValidTimezone(timezone) ? timezone : 'UTC';
}

function pad2(value: number) {
  return String(value).padStart(2, '0');
}
